import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    TextField,
    MenuItem,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper,
    Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CommonButton from '../../../components/atom/CommonButton';
import { useAuth } from '../../../hooks/useAuth';
import useNotify from '../../../hooks/useNotify';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { migrateCommissionGroupPaymentType } from '@openapi/commission-groups/commission-groups';

const CASH_METHOD_TYPE = 'cash';

// Bulk-fixes legacy bookings that still sit on a generic affiliate payment
// type: moves this group's bookings onto one of the group's own payment
// methods so they attribute correctly on the affiliate invoice. Rendered as a
// plain section (no Paper wrapper) to align with the sibling form sections.
function MigratePaymentTypePanel({ commissionGroupId, venuePaymentMethods, paymentMethodLinks }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { notifyError, notifySuccess } = useNotify();
    const [fromSlug, setFromSlug] = useState('');
    const [toSlug, setToSlug] = useState('');
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    const linkedIds = new Set(paymentMethodLinks.map((l) => l.paymentMethodId));
    const cashMethods = venuePaymentMethods.filter((pm) => pm.methodType === CASH_METHOD_TYPE);
    // Source: legacy cash methods NOT linked to this group.
    const fromOptions = cashMethods.filter((pm) => !linkedIds.has(pm.id));
    // Target: this group's own cash methods.
    const toOptions = cashMethods.filter((pm) => linkedIds.has(pm.id));
    const noOptions = fromOptions.length === 0 || toOptions.length === 0;

    const callMigration = (dryRun) =>
        migrateCommissionGroupPaymentType(Number(commissionGroupId), {
            fromPaymentType: fromSlug,
            toPaymentType: toSlug,
            ...(dryRun ? { dryRun: true } : {}),
        });

    const runPreview = async () => {
        try {
            setBusy(true);
            setPreview(await callMigration(true));
        } catch (err) {
            notifyError(err);
        } finally {
            setBusy(false);
        }
    };

    // The preview dialog is itself the confirmation step.
    const runMigrate = async () => {
        try {
            setBusy(true);
            const res = await callMigration(false);
            notifySuccess(`Migrated ${res.total} transaction(s).`);
            setPreview(null);
        } catch (err) {
            notifyError(err);
        } finally {
            setBusy(false);
        }
    };

    // Bulk affiliate migration is a destructive, hard-to-audit operation —
    // restrict to superadmins only, even from non-superadmins who happen to
    // hold the bookings.re_source capability.
    if (!user?.isSuperAdmin) {
        return null;
    }

    return (
        <>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('commissionGroups.migratePayments.title', 'Migrate legacy affiliate payments')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t(
                    'commissionGroups.migratePayments.description',
                    "Move this group's bookings off a generic payment type onto one of the group's own payment methods, so their money attributes correctly on the affiliate invoice.",
                )}
            </Typography>
            {noOptions ? (
                <Typography variant="body2" color="text.secondary">
                    Needs at least one cash payment method that is not linked to this group (the
                    legacy type) and one that is (the target).
                </Typography>
            ) : (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="From (legacy)"
                        value={fromSlug}
                        onChange={(e) => setFromSlug(e.target.value)}
                    >
                        {fromOptions.map((pm) => (
                            <MenuItem key={pm.id} value={pm.slug}>
                                {pm.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="To (this group)"
                        value={toSlug}
                        onChange={(e) => setToSlug(e.target.value)}
                    >
                        {toOptions.map((pm) => (
                            <MenuItem key={pm.id} value={pm.slug}>
                                {pm.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <CommonButton
                        buttonType="secondary"
                        label="Preview"
                        disabled={busy || !fromSlug || !toSlug}
                        onClick={runPreview}
                    />
                </Stack>
            )}

            <Dialog
                open={!!preview}
                onClose={() => !busy && setPreview(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {t(
                        'commissionGroups.migratePayments.title',
                        'Migrate legacy affiliate payments',
                    )}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {preview?.total ?? 0} transaction(s) will be migrated.
                    </Typography>
                    {preview?.total > 0 && (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Booking</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell>Invoiced</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {preview.bookings.map((b) => (
                                        <TableRow key={b.transactionId}>
                                            <TableCell>#{b.bookingId}</TableCell>
                                            <TableCell>{formatCentsToUsd(b.amount)}</TableCell>
                                            <TableCell>
                                                {b.alreadyInvoiced ? (
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        label="Invoiced"
                                                    />
                                                ) : (
                                                    '–'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <CommonButton
                        buttonType="secondary"
                        label={t('common.cancel')}
                        disabled={busy}
                        onClick={() => setPreview(null)}
                    />
                    {preview?.total > 0 && (
                        <CommonButton
                            buttonType="primary"
                            label={`Migrate ${preview.total} transaction(s)`}
                            disabled={busy}
                            onClick={runMigrate}
                        />
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

export default MigratePaymentTypePanel;
